/* Atelier - the birthstone gem.

   A real 3D object, raymarched in a fragment shader on one quad. No geometry buffers and no
   library: the whole thing is this file, and it renders into the decorative orb the
   birthstone section already has, so no template changes and the existing gradient stays
   visible underneath as the fallback.

   Why not three.js and a model: there is no model. Every one of the 178 products is
   photographs - checked, and not one carries MODEL_3D media - so a scene graph would have
   nothing to put in it.

   Where it ended up is better than where it was aimed. The orb lives in "Find your
   birthstone", and that section already writes the selected month's colour onto the orb as
   an inline --g custom property. The shader reads it, so the stone the visitor is turning is
   the stone they just picked: sapphire blue in September, emerald in May. Changing month
   retints it, because the inline style is watched.

   Cost: about 6KB, one canvas at 340px, capped at 1.5x device pixel ratio. It stops
   rendering when it scrolls out of view or the tab is hidden, and it never starts on a phone,
   on a machine reporting fewer than four cores, or with reduced motion set.

   One frame is drawn synchronously at setup rather than waiting for requestAnimationFrame,
   because rAF does not run in a background tab: opening the page in a new tab would otherwise
   have left a blank canvas sitting over the gradient until the tab was looked at. */
(function () {
  'use strict';

  var FALLBACK_TINT = [0.42, 0.06, 0.165];   // --pdp-claret, for an orb with no colour set

  var VERT = [
    'attribute vec2 p;',
    'void main(){ gl_Position = vec4(p, 0.0, 1.0); }'
  ].join('');

  var FRAG = [
    'precision highp float;',
    'uniform vec2 u_res;',
    'uniform float u_time;',
    'uniform vec2 u_ptr;',
    'uniform float u_scroll;',
    'uniform vec3 u_tint;',
    'mat3 R;',

    'mat3 rotY(float a){ float c=cos(a), s=sin(a); return mat3(c,0.0,-s, 0.0,1.0,0.0, s,0.0,c); }',
    'mat3 rotX(float a){ float c=cos(a), s=sin(a); return mat3(1.0,0.0,0.0, 0.0,c,-s, 0.0,s,c); }',

    // An octahedron clipped by a sphere: flat facets where they meet the sphere, which is
    // roughly how a cut stone reads at this size.
    'float sdOcta(vec3 p, float s){ p = abs(p); return (p.x+p.y+p.z-s)*0.5773502692; }',
    'float map(vec3 q){ vec3 p = R*q; return max(sdOcta(p,1.15), length(p)-0.92); }',

    'vec3 normalAt(vec3 p){',
    '  vec2 e = vec2(0.0018, 0.0);',
    '  return normalize(vec3(',
    '    map(p+e.xyy)-map(p-e.xyy),',
    '    map(p+e.yxy)-map(p-e.yxy),',
    '    map(p+e.yyx)-map(p-e.yyx)));',
    '}',

    // The environment the stone reflects, built from the selected birthstone colour: the
    // colour itself darkened for the body, the colour lifted toward white for the light, and
    // a warm rim. Plus a moving sparkle band, which is what sells it as faceted rather than
    // as a blob.
    'vec3 env(vec3 d){',
    '  float y = d.y*0.5 + 0.5;',
    '  vec3 deep  = u_tint * 0.42;',
    '  vec3 light = mix(u_tint, vec3(1.0), 0.78);',
    '  vec3 rim   = mix(u_tint, vec3(1.0, 0.86, 0.82), 0.45);',
    '  vec3 c = mix(deep, light, smoothstep(0.08, 0.92, y));',
    '  c = mix(c, rim, 0.30*smoothstep(0.35, 1.0, abs(d.x)));',
    '  c += 0.30*pow(max(0.0, sin(d.x*14.0 + d.y*9.0 + u_time*0.55)), 18.0);',
    '  return c;',
    '}',

    'void main(){',
    '  vec2 uv = (gl_FragCoord.xy*2.0 - u_res) / min(u_res.x, u_res.y);',
    '  R = rotY(u_time*0.22 + u_ptr.x*0.55) * rotX(-u_ptr.y*0.40 + u_scroll*0.70);',

    '  vec3 ro = vec3(0.0, 0.0, 3.1);',
    '  vec3 rd = normalize(vec3(uv, -1.9));',

    '  float t = 0.0;',
    '  bool hit = false;',
    '  for (int i = 0; i < 64; i++) {',
    '    float d = map(ro + rd*t);',
    '    if (d < 0.0016) { hit = true; break; }',
    '    t += d;',
    '    if (t > 6.0) break;',
    '  }',

    // Miss: fully transparent, so the orb's own gradient is what shows.
    '  if (!hit) { gl_FragColor = vec4(0.0); return; }',

    '  vec3 p = ro + rd*t;',
    '  vec3 n = normalAt(p);',
    '  float fres = pow(1.0 - max(0.0, dot(-rd, n)), 3.0);',

    '  vec3 refl = env(reflect(rd, n));',
    '  vec3 refr = env(refract(rd, n, 0.62));',
    '  vec3 col = mix(refr, refl, clamp(fres*1.25, 0.0, 1.0));',

    // Facet edges: the rim where the surface turns away from the camera.
    '  float edge = pow(1.0 - abs(dot(n, -rd)), 8.0);',
    '  col += vec3(1.0, 0.93, 0.88) * edge * 0.45;',
    '  col = pow(col, vec3(0.92));',

    '  float a = clamp(0.62 + fres*0.52, 0.0, 1.0);',
    // Premultiplied, matching the context created below.
    '  gl_FragColor = vec4(col*a, a);',
    '}'
  ].join('');

  function compile(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  /* The section writes the month's colour as a hex literal, e.g. style="--g: #2A4FC4". */
  function tintOf(host) {
    var raw = (getComputedStyle(host).getPropertyValue('--g') || '').trim();
    var m = raw.match(/^#([0-9a-f]{6})$/i);
    if (!m) return FALLBACK_TINT;
    var n = parseInt(m[1], 16);
    return [
      ((n >> 16) & 255) / 255,
      ((n >> 8) & 255) / 255,
      (n & 255) / 255
    ];
  }

  function allowed() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (!window.matchMedia('(min-width: 750px)').matches) return false;
    if ((navigator.hardwareConcurrency || 8) < 4) return false;
    if (navigator.deviceMemory && navigator.deviceMemory < 4) return false;
    return true;
  }

  function start(host) {
    var canvas = document.createElement('canvas');
    canvas.className = 't3-gem';
    canvas.setAttribute('aria-hidden', 'true');

    var gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      depth: false,
      powerPreference: 'low-power'
    });
    if (!gl) return;

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;

    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 3,-1, -1,3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uRes = gl.getUniformLocation(prog, 'u_res');
    var uTime = gl.getUniformLocation(prog, 'u_time');
    var uPtr = gl.getUniformLocation(prog, 'u_ptr');
    var uScroll = gl.getUniformLocation(prog, 'u_scroll');
    var uTint = gl.getUniformLocation(prog, 'u_tint');

    host.appendChild(canvas);

    var ptrX = 0, ptrY = 0, tgtX = 0, tgtY = 0, scroll = 0;
    var tint = tintOf(host);
    var tintTo = tint.slice();
    var visible = true, running = false, raf = null;
    var t0 = performance.now();

    function size() {
      var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      var r = host.getBoundingClientRect();
      var w = Math.max(1, Math.round(r.width * dpr));
      var h = Math.max(1, Math.round(r.height * dpr));
      if (canvas.width === w && canvas.height === h) return;
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
    }

    function renderFrame() {
      size();

      // Eased toward the pointer, so the stone turns rather than snapping.
      ptrX += (tgtX - ptrX) * 0.06;
      ptrY += (tgtY - ptrY) * 0.06;

      // Eased between birthstones too, so picking a month is a colour change rather than a
      // cut.
      tint[0] += (tintTo[0] - tint[0]) * 0.08;
      tint[1] += (tintTo[1] - tint[1]) * 0.08;
      tint[2] += (tintTo[2] - tint[2]) * 0.08;

      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, (performance.now() - t0) / 1000);
      gl.uniform2f(uPtr, ptrX, ptrY);
      gl.uniform1f(uScroll, scroll);
      gl.uniform3f(uTint, tint[0], tint[1], tint[2]);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function loop() {
      raf = null;
      if (!visible || document.hidden) { running = false; return; }
      renderFrame();
      raf = window.requestAnimationFrame(loop);
    }

    function run() {
      if (running) return;
      running = true;
      raf = window.requestAnimationFrame(loop);
    }

    new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) run();
      else if (raf) { window.cancelAnimationFrame(raf); raf = null; running = false; }
    }, { rootMargin: '120px' }).observe(host);

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && visible) run();
    });

    /* The month buttons rewrite the orb's inline --g. */
    new MutationObserver(function () {
      tintTo = tintOf(host);
      run();
    }).observe(host, { attributes: true, attributeFilter: ['style'] });

    window.addEventListener('pointermove', function (e) {
      tgtX = (e.clientX / window.innerWidth) * 2 - 1;
      tgtY = (e.clientY / window.innerHeight) * 2 - 1;
    }, { passive: true });

    window.addEventListener('scroll', function () {
      var r = host.getBoundingClientRect();
      scroll = Math.max(-1, Math.min(1, -r.top / Math.max(1, window.innerHeight)));
    }, { passive: true });

    // Lost context, on a laptop waking from sleep or a GPU reset, would otherwise leave a
    // blank canvas over the gradient.
    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      canvas.remove();
    });

    // First frame now, not on the next animation frame. Then fade in - also on a timer
    // rather than a rAF, for the background-tab case.
    renderFrame();
    window.setTimeout(function () { canvas.classList.add('is-in'); }, 30);

    // Exposed so a frame can be driven from the console when checking the shader; the render
    // loop never calls it from outside.
    window.AtelierGem = { frame: renderFrame, canvas: canvas, gl: gl };

    run();
  }

  function init() {
    if (!allowed()) return;
    var host = document.querySelector('.gf-gem__orb');
    if (!host) return;
    if (host.querySelector('.t3-gem')) return;
    start(host);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();